/**
 * Auditoria de faixas de CEP (cerca virtual).
 * Espelha o first-match do e-commerce (region_key ASC, ordem ASC, id ASC).
 * Manter alinhado a xpro4api/src/utils/cepRangeAudit.js
 */

export function onlyDigits(s) {
  return String(s ?? "").replace(/\D+/g, "");
}

export function toIntCep(s, def = -1) {
  const d = onlyDigits(s);
  if (!d) return def;
  const n = Number(d);
  return Number.isFinite(n) ? Math.trunc(n) : def;
}

export function padCep8(n) {
  if (!Number.isFinite(n) || n < 0) return "";
  return String(n).padStart(8, "0");
}

export function isRangeActive(r) {
  const s = r && r.status;
  if (s === 0 || s === "0") return false;
  return true;
}

export function intervalsOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart <= bEnd && bStart <= aEnd;
}

export function isRangeContained(inner, outer) {
  return inner.start >= outer.start && inner.end <= outer.end;
}

export function compareProductionOrder(a, b) {
  const rk = String(a.region_key || "").localeCompare(String(b.region_key || ""), "en");
  if (rk !== 0) return rk;
  const oa = Number.isFinite(+a.ordem) ? +a.ordem : 0;
  const ob = Number.isFinite(+b.ordem) ? +b.ordem : 0;
  if (oa !== ob) return oa - ob;
  const ia = Number.isFinite(+a.id) ? +a.id : 0;
  const ib = Number.isFinite(+b.id) ? +b.id : 0;
  return ia - ib;
}

export function normalizeRange(r, idx) {
  const startDigits = onlyDigits(r.start_cep || r.start || "");
  const endDigits = onlyDigits(r.end_cep || r.end || "");
  const region_key = String(r.region_key || r.regionKey || "").trim();
  const target = String(
    r.target_cod_interno || r.target_codigo_interno || r.company || ""
  ).trim();
  const ordem = Number.isFinite(+r.ordem) ? +r.ordem : idx;
  const rawId = r.id != null && r.id !== "" ? Number(r.id) : NaN;
  return {
    _idx: idx,
    _id: r._id,
    id: Number.isFinite(rawId) ? rawId : idx,
    region_key,
    start_cep: startDigits,
    end_cep: endDigits,
    start: toIntCep(startDigits),
    end: toIntCep(endDigits),
    target_cod_interno: target,
    ordem,
    status: isRangeActive(r) ? 1 : 0,
  };
}

function summarize(r) {
  return {
    id: r.id,
    _id: r._id,
    _idx: r._idx,
    region_key: r.region_key,
    start_cep: r.start_cep,
    end_cep: r.end_cep,
    target_cod_interno: r.target_cod_interno,
    status: r.status,
    ordem: r.ordem,
  };
}

export function findCompanyByCep(cep, ranges) {
  const n = toIntCep(cep, -1);
  if (n < 0) return null;
  const ordered = (ranges || [])
    .map((r, i) => normalizeRange(r, i))
    .filter(isRangeActive)
    .sort(compareProductionOrder);
  for (const r of ordered) {
    if (r.start >= 0 && r.end >= 0 && n >= r.start && n <= r.end) {
      return {
        company: r.target_cod_interno,
        regionKey: r.region_key,
        range: { start: r.start_cep, end: r.end_cep },
        matched: summarize(r),
      };
    }
  }
  return null;
}

export function findActiveOverlaps(candidate, ranges, { ignoreIdx } = {}) {
  const c = normalizeRange(candidate, ignoreIdx != null ? ignoreIdx : -1);
  if (c.start < 0 || c.end < 0 || c.start > c.end) return [];
  const out = [];
  (ranges || []).forEach((raw, i) => {
    if (ignoreIdx != null && i === ignoreIdx) return;
    const r = normalizeRange(raw, i);
    if (c._id && r._id && String(c._id) === String(r._id)) return;
    if (!isRangeActive(r)) return;
    if (r.start < 0 || r.end < 0 || r.start > r.end) return;
    if (intervalsOverlap(c.start, c.end, r.start, r.end)) out.push(summarize(r));
  });
  return out;
}

export function auditCepRanges(ranges, opts = {}) {
  const { regionKeys, targetSlugsByRegion } = opts;
  const findings = [];
  const normalized = (ranges || []).map((r, i) => normalizeRange(r, i));

  for (const r of normalized) {
    const issues = [];
    if (!r.region_key) issues.push("marca vazia");
    if (!/^\d{8}$/.test(r.start_cep)) issues.push("CEP inicial inválido");
    if (!/^\d{8}$/.test(r.end_cep)) issues.push("CEP final inválido");
    if (/^\d{8}$/.test(r.start_cep) && /^\d{8}$/.test(r.end_cep) && r.start > r.end) {
      issues.push("CEP inicial maior que o final");
    }
    if (!r.target_cod_interno) issues.push("destino vazio");
    if (issues.length) {
      findings.push({
        type: "invalid",
        severity: "block",
        message: issues.join("; "),
        ranges: [summarize(r)],
      });
    }
  }

  if (Array.isArray(regionKeys)) {
    const regionSet = new Set(regionKeys.map((k) => String(k || "").trim()).filter(Boolean));
    for (const r of normalized) {
      if (!r.region_key) continue;
      if (!regionSet.has(r.region_key)) {
        findings.push({
          type: "orphan_region",
          severity: "warn",
          message: `Marca "${r.region_key}" não está em Regiões do projeto.`,
          ranges: [summarize(r)],
        });
      }
    }
  }

  const slugsByRegion =
    targetSlugsByRegion && typeof targetSlugsByRegion === "object" && !Array.isArray(targetSlugsByRegion)
      ? targetSlugsByRegion
      : null;
  if (slugsByRegion) {
    const orphanByDest = new Map();
    for (const r of normalized) {
      if (!r.target_cod_interno || !r.region_key) continue;
      const list = slugsByRegion[r.region_key];
      if (!Array.isArray(list)) continue;
      const targetSet = new Set(list.map((k) => String(k || "").trim().toLowerCase()).filter(Boolean));
      if (targetSet.has(r.target_cod_interno.toLowerCase())) continue;
      const key = `${r.region_key}|${r.target_cod_interno.toLowerCase()}`;
      if (!orphanByDest.has(key)) {
        orphanByDest.set(key, {
          region_key: r.region_key,
          target: r.target_cod_interno,
          ranges: [],
        });
      }
      orphanByDest.get(key).ranges.push(summarize(r));
    }
    for (const item of orphanByDest.values()) {
      const n = item.ranges.length;
      findings.push({
        type: "orphan_target",
        severity: "warn",
        message: `Destino "${item.target}" não corresponde a nenhuma empresa no workspace da marca "${item.region_key}"${n > 1 ? ` (${n} faixas)` : ""}.`,
        ranges: item.ranges.slice(0, 8),
        count: n,
      });
    }
  }

  for (let i = 0; i < normalized.length; i++) {
    const a = normalized[i];
    if (a.start < 0 || a.end < 0 || a.start > a.end) continue;
    for (let j = i + 1; j < normalized.length; j++) {
      const b = normalized[j];
      if (b.start < 0 || b.end < 0 || b.start > b.end) continue;
      if (!intervalsOverlap(a.start, a.end, b.start, b.end)) continue;

      const bothActive = isRangeActive(a) && isRangeActive(b);
      const sameDest =
        a.region_key === b.region_key &&
        a.target_cod_interno.toLowerCase() === b.target_cod_interno.toLowerCase();
      const interStart = Math.max(a.start, b.start);
      const interEnd = Math.min(a.end, b.end);
      const winnerSrc = compareProductionOrder(a, b) <= 0 ? a : b;

      if (!bothActive) {
        findings.push({
          type: "inactive_overlap",
          severity: "info",
          message: `Sobreposição envolvendo faixa inativa (${padCep8(interStart)}–${padCep8(interEnd)}).`,
          ranges: [summarize(a), summarize(b)],
          intersection: { start: padCep8(interStart), end: padCep8(interEnd) },
        });
        continue;
      }

      const loserSrc = winnerSrc === a ? b : a;
      const loserFullyUnused = isRangeContained(loserSrc, winnerSrc);

      findings.push({
        type: sameDest ? "overlap_redundant" : "overlap_conflict",
        severity: "block",
        message: sameDest
          ? `Faixas ativas da mesma marca/destino se sobrepõem (${padCep8(interStart)}–${padCep8(interEnd)}).`
          : `Faixas ativas de marcas ou destinos diferentes se sobrepõem (${padCep8(interStart)}–${padCep8(interEnd)}). Vencedor atual no e-commerce: ${winnerSrc.region_key} → ${winnerSrc.target_cod_interno}.`,
        ranges: [summarize(a), summarize(b)],
        intersection: { start: padCep8(interStart), end: padCep8(interEnd) },
        winner: summarize(winnerSrc),
        unused: loserFullyUnused ? [summarize(loserSrc)] : [],
        unusedPartial: loserFullyUnused ? [] : [summarize(loserSrc)],
      });
    }
  }

  const blocking = findings.filter((f) => f.severity === "block");
  const warnings = findings.filter((f) => f.severity === "warn");
  const infos = findings.filter((f) => f.severity === "info");
  const activeCount = normalized.filter(isRangeActive).length;
  const ordered = [...blocking, ...warnings, ...infos];

  return {
    findings: ordered,
    summary: {
      total: normalized.length,
      active: activeCount,
      blocking: blocking.length,
      warn: warnings.length,
      info: infos.length,
    },
    hasBlocking: blocking.length > 0,
  };
}

export function fingerprintCepRanges(ranges) {
  const rows = (ranges || []).map((r, i) => {
    const n = normalizeRange(r, i);
    return {
      region_key: n.region_key,
      start_cep: n.start_cep,
      end_cep: n.end_cep,
      target_cod_interno: n.target_cod_interno,
      status: n.status,
      ordem: n.ordem,
    };
  });
  return JSON.stringify(rows);
}
